import { useState } from 'react';
import { Button } from '@/components/ui/button';

import { useTranslations } from '@/i18n/use-locale';
import type { ModerationSubject } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

export type ConfirmMode = 'approve' | 'reject';

/**
 * The inline confirmation under a queued item: approving asks once, rejecting asks for
 * the compulsory reason the requester is shown.
 *
 * Written three times before — the two queue lists and the review screen — with the
 * lists' reject panel missing its label, its hint and its cancel, and its confirm still
 * live while the decision was in flight. One panel now, worded from the subject's own
 * catalogue (`admin.verification` for a registration, `admin.documents` for a credential
 * document), so an admin meets the same flow wherever they decide. The screen keeps its
 * own trigger buttons and which panel is open; the reason lives here, and goes when the
 * panel closes.
 */
export function ReasonConfirm({
  subject,
  mode,
  name,
  pending,
  onApprove,
  onReject,
  onCancel,
  className,
  approveBody,
  rejectHint,
}: {
  subject: ModerationSubject;
  mode: ConfirmMode;
  /** What the confirmation names: the organization, or the credential's title. */
  name: string;
  pending: boolean;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onCancel: () => void;
  className?: string;
  /**
   * Wording for an item the subject's default does not describe — a video introduction
   * is shown to clubs, never on the public profile, and backs no item to delete.
   */
  approveBody?: string;
  rejectHint?: string;
}) {
  const t = useTranslations(subject === 'organization' ? 'admin.verification' : 'admin.documents');
  const [reason, setReason] = useState('');

  if (mode === 'approve') {
    return (
      <div
        role="group"
        aria-label={t('confirmApprove.title')}
        className={cn('space-y-2 rounded-lg border border-pitch/30 bg-pitch/5 p-3', className)}
      >
        <p className="text-[12.5px] text-navy">
          {approveBody ?? t('confirmApprove.body', { name, title: name })}
        </p>
        <div className="flex gap-2">
          <Button type="button" size="xs" disabled={pending} onClick={onApprove}>
            {t('confirmApprove.title')}
          </Button>
          <Button type="button" variant="outline" size="xs" onClick={onCancel}>
            {t('reject.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={t('reject.label')}
      className={cn('space-y-2 rounded-lg border border-rose-200 bg-rose-50/60 p-3', className)}
    >
      <p className="text-[12px] text-conc-500">{rejectHint ?? t('reject.hint')}</p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('reject.placeholder')}
        aria-label={t('reject.label')}
        className="w-full rounded border border-conc-100 bg-white p-2 text-xs"
      />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="danger"
          size="xs"
          disabled={!reason.trim() || pending}
          onClick={() => onReject(reason.trim())}
        >
          {t('reject.confirm')}
        </Button>
        <Button type="button" variant="outline" size="xs" onClick={onCancel}>
          {t('reject.cancel')}
        </Button>
      </div>
    </div>
  );
}
